import React, { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { useLocation } from "react-router";
import Modal, { MODAL_SIZE } from "components/Modal";
import { ProfileBox, ProfileCodeChallengeList, ProfileVideoInterviewsList } from "components/Profile";
import Tabs from "components/Tabs";
import { Grid } from "components/Containers";
import { breakpoints, margins } from "style";
import { useAuth } from "hooks";
import {
  ApplicantCandidateInsights,
  ApplicationPreviousAssignments,
  ApplicationSummary,
  ApplicantTrustAndVerification,
  ApplicantSkillsEvaluation,
} from "components/Missions/Applicants";
// Fixme: Importing USER_TYPES from constants as a named export is giving errors
import USER_TYPES from "constants/userTypes";

/**
 * ProfileModal
 *
 * @param {String}    id
 * @param {Boolean}   show
 * @param {Function}  handleClose
 * @param {Function}  handlePrimary
 * @param {Number}    initialTab
 * @param {Object}    tabTitles
 * @param {ReactNode} tabActions Optional actions displayed beside the tabs
 */
const ProfileModal = ({
  id,
  show,
  handleClose,
  handlePrimary,
  initialTab = 0,
  tabTitles = {},
  tabActions,
  ...props
}) => {
  const location = useLocation();
  const { isAdmin, roles } = useAuth();

  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState();

  useEffect(() => {
    setTabs(filterTabsByRoles(TABS, roles, location.pathname));
  }, [location.pathname, roles]);

  // Go back to default tab when modal opens
  useEffect(() => {
    if (show) {
      setSelectedTab(tabs[initialTab]?.key);
    }
  }, [show, initialTab, tabs]);

  const Components = {
    [TABS.summary.key]: <ApplicationSummary {...props} />,
    [TABS.profile.key]: (
      <ProfileBox id={id} isEditable={false} marginSize={margins.normal} showAiNoteSummary={isAdmin} />
    ),
    [TABS.candidateInsights.key]: <ApplicantCandidateInsights profileId={id} {...props} />,
    [TABS.skillsEvaluation.key]: <ApplicantSkillsEvaluation profileId={id} {...props} />,
    [TABS.codeChallenges.key]: <ProfileCodeChallengeList profileId={id} />,
    [TABS.interviews.key]: <ProfileVideoInterviewsList profileId={id} />,
    [TABS.assignments.key]: <ApplicationPreviousAssignments profileId={id} />,
    [TABS.trustVerification.key]: <ApplicantTrustAndVerification {...props} />,
  };

  return (
    <Modal
      title={<FormattedMessage id="ClientSearch.ClientSearchProfilePreview.Title" />}
      size={MODAL_SIZE.large}
      show={show}
      showSave={true}
      showCancel={true}
      secondaryButtonMessageId="Global.Close"
      primaryButtonMessageId="Profile.ProfileModal.ButtonViewProfile"
      canSubmit={true}
      onSubmit={handlePrimary}
      onClose={handleClose}
      clickOutsideToClose={true}
      centered={false}
      {...props}
    >
      <Grid>
        <Grid.col start={1} end={13} css={tabActions && styles.tabsRow}>
          <Tabs
            data={tabs.map((tab) => tabTitles?.[tab.key] || tab.title)}
            selected={tabs.findIndex((tab) => tab.key === selectedTab)}
            onSelect={(index) => setSelectedTab(tabs[index]?.key)}
            marginSize={margins.none}
            css={tabActions && styles.tabs}
          />
          {tabActions && <div css={styles.tabActions}>{tabActions}</div>}
        </Grid.col>
        <Grid.col start={1} end={13}>
          {Components[selectedTab]}
        </Grid.col>
      </Grid>
    </Modal>
  );
};

const styles = {
  tabsRow: css`
    display: flex;
    align-items: flex-start;
    gap: ${margins.xsmall};

    @media (max-width: ${breakpoints.mobile}) {
      flex-direction: column;
    }
  `,
  tabs: css`
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
  `,
  tabActions: css`
    flex-shrink: 0;

    @media (max-width: ${breakpoints.mobile}) {
      order: -1;
      width: 100%;
    }
  `,
};

const TABS = {
  summary: {
    title: "Summary",
    key: "summary",
    visibleTo: [USER_TYPES.client, USER_TYPES.showcase],
    visibleInPaths: ["/client/applicants", "/client/board"],
  },
  profile: {
    title: "Profile",
    key: "profile",
    visibleTo: [
      USER_TYPES.admin,
      USER_TYPES.client,
      USER_TYPES.showcase,
      USER_TYPES.recruiter,
      USER_TYPES.partner,
      USER_TYPES.signal,
    ],
    visibleInPaths: ["*"],
  },
  candidateInsights: {
    title: "Candidate Insights",
    key: "candidate_insights",
    visibleTo: [USER_TYPES.client],
    visibleInPaths: ["/client/applicants", "/client/board"],
  },
  skillsEvaluation: {
    title: "Skills Evaluation",
    key: "skills_evaluation",
    visibleTo: [USER_TYPES.client],
    visibleInPaths: ["/client/applicants", "/client/board"],
  },
  codeChallenges: {
    title: "Code Challenges",
    key: "code_challenges",
    visibleTo: [USER_TYPES.admin, USER_TYPES.showcase],
    visibleInPaths: ["*"],
  },
  interviews: {
    title: "Interviews",
    key: "interviews",
    visibleTo: [USER_TYPES.admin, USER_TYPES.showcase, USER_TYPES.recruiter, USER_TYPES.partner],
    visibleInPaths: ["*"],
  },
  assignments: {
    title: "Assignments",
    key: "assignments",
    visibleTo: [USER_TYPES.admin],
    visibleInPaths: ["*"],
  },
  trustVerification: {
    title: "Trust & Verification",
    key: "trust_verification",
    visibleTo: [USER_TYPES.client],
    visibleInPaths: ["/client/applicants", "/client/board"],
  },
};

const isPathAllowed = (allowedPaths, currentPath) => {
  return allowedPaths.some((path) => path === "*" || currentPath.startsWith(path));
};

const filterTabsByRoles = (tabs, roles, pathname) => {
  const roleNames = (roles || []).map(({ name }) => name);

  return Object.values(tabs)
    .filter((tab) => tab.visibleTo.some((role) => roleNames.includes(role)))
    .filter((tab) => isPathAllowed(tab.visibleInPaths, pathname));
};

ProfileModal.propTypes = {
  id: PropTypes.string.isRequired,
  show: PropTypes.bool,
  handleClose: PropTypes.func,
  handlePrimary: PropTypes.func,
  initialTab: PropTypes.number,
  tabTitles: PropTypes.object,
  tabActions: PropTypes.node,
};

export default ProfileModal;
